import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { termsApi, type Term } from '../features/terms/api.ts';
import { dv } from '../i18n/dv.ts';
import { formatDvDate } from '../lib/dvDate.ts';
import { type Body } from '../lib/body.ts';
import { BodyToggle } from '../features/orgSettings/BodyToggle.tsx';

function termLabel(n: number): string {
  return `${n} ވަނަ ދައުރު`;
}

export function TermSettings(): JSX.Element {
  const qc = useQueryClient();
  // Council/WDC toggle: the selected body is the query key + the body sent to the
  // term endpoints, so each body has its own term list. Council is the default.
  const [body, setBody] = useState<Body>('COUNCIL');
  const { data: terms, isLoading } = useQuery({
    queryKey: ['terms', body],
    queryFn: () => termsApi.list(body),
  });

  const [number, setNumber] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [createError, setCreateError] = useState<'exists' | 'generic' | null>(null);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['terms'] });
    void qc.invalidateQueries({ queryKey: ['org'] });
    void qc.invalidateQueries({ queryKey: ['meetings'] });
  };

  // Inline edit state for one term at a time.
  const [editing, setEditing] = useState<Term | null>(null);
  const [eNumber, setENumber] = useState('');
  const [eStart, setEStart] = useState('');
  const [eEnd, setEEnd] = useState('');
  function startEdit(t: Term): void {
    setEditing(t);
    setENumber(String(t.number));
    setEStart(t.startDate ? t.startDate.slice(0, 10) : '');
    setEEnd(t.endDate ? t.endDate.slice(0, 10) : '');
  }
  const update = useMutation({
    mutationFn: (id: string) =>
      termsApi.update(id, {
        number: Number(eNumber),
        startDate: eStart || null,
        endDate: eEnd || null,
      }, body),
    onSuccess: () => { setEditing(null); invalidate(); },
  });

  const create = useMutation({
    mutationFn: () =>
      termsApi.create({
        number: Number(number),
        ...(startDate ? { startDate: new Date(startDate).toISOString() } : {}),
        ...(endDate ? { endDate: new Date(endDate).toISOString() } : {}),
      }, body),
    onSuccess: () => {
      setNumber(''); setStartDate(''); setEndDate(''); setCreateError(null);
      invalidate();
    },
    onError: (e) => {
      setCreateError(axios.isAxiosError(e) && e.response?.status === 409 ? 'exists' : 'generic');
    },
  });

  const activate = useMutation({
    mutationFn: (id: string) => termsApi.activate(id, body),
    onSuccess: () => invalidate(),
  });

  const remove = useMutation({
    mutationFn: (id: string) => termsApi.remove(id, body),
    onSuccess: () => invalidate(),
  });

  if (isLoading || !terms) {
    return (
      <div className="space-y-6 lg:space-y-8">
        <BodyToggle body={body} onChange={setBody} />
        <p className="text-sm text-muted-foreground">{dv.common.loading}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 lg:space-y-8">
      <BodyToggle body={body} onChange={setBody} />
      <section className="space-y-3 rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6">
        <h2 className="status-label text-muted-foreground">{dv.settings.addTerm}</h2>
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => { e.preventDefault(); setCreateError(null); create.mutate(); }}
        >
          <div className="space-y-1">
            <label className="field-label">{dv.settings.termNumberLabel}</label>
            <input
              required type="number" min={1} max={99} dir="ltr"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              className="input w-28"
            />
          </div>
          <div className="space-y-1">
            <label className="field-label">{dv.settings.termStart}</label>
            <input
              type="date" dir="ltr"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="input"
            />
          </div>
          <div className="space-y-1">
            <label className="field-label">{dv.settings.termEnd}</label>
            <input
              type="date" dir="ltr"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="input"
            />
          </div>
          <button
            type="submit"
            disabled={create.isPending}
            className="btn-primary"
          >
            {dv.common.save}
          </button>
          {createError && <span className="text-sm text-destructive">{createError === 'exists' ? dv.settings.termExists : dv.common.error}</span>}
        </form>
      </section>

      <section className="divide-y divide-black/[0.05] overflow-hidden rounded-3xl bg-[hsl(var(--chrome))]">
        {terms.length === 0 && (
          <p className="p-5 text-sm text-muted-foreground">{dv.settings.noTerms}</p>
        )}
        {terms.map((t: Term) => (
          <div key={t.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3.5 sm:px-5">
            <div>
              <p className="font-medium">
                {termLabel(t.number)}
                {t.isActive && (
                  <span className="pill pill-success ms-2 align-middle">
                    {dv.settings.activeTerm}
                  </span>
                )}
              </p>
              <p className="text-sm text-muted-foreground">
                {t.startDate ? formatDvDate(t.startDate) : '—'}
                {' '}–{' '}
                {t.endDate ? formatDvDate(t.endDate) : '—'}
                {' · '}
                {t.meetingCount} {dv.settings.termMeetings}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => (editing?.id === t.id ? setEditing(null) : startEdit(t))}
                className="btn-secondary btn-sm"
              >
                {dv.common.edit}
              </button>
              {!t.isActive && (
                <button
                  type="button"
                  disabled={activate.isPending}
                  onClick={() => { if (window.confirm(dv.settings.makeActive)) activate.mutate(t.id); }}
                  className="btn-secondary btn-sm"
                >
                  {dv.settings.makeActive}
                </button>
              )}
              {!t.isActive && t.meetingCount === 0 && (
                <button
                  type="button"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(t.id)}
                  className="btn-danger btn-sm"
                >
                  {dv.common.delete}
                </button>
              )}
            </div>
            {editing?.id === t.id && (
              <form
                className="flex w-full flex-wrap items-end gap-3 border-t border-black/[0.05] pt-3"
                onSubmit={(e) => { e.preventDefault(); update.mutate(t.id); }}
              >
                <div className="space-y-1">
                  <label className="block text-xs text-muted-foreground">{dv.settings.termNumberLabel}</label>
                  <input type="number" min={1} max={99} dir="ltr" required value={eNumber}
                    onChange={(e) => setENumber(e.target.value)}
                    className="input w-24" />
                </div>
                <div className="space-y-1">
                  <label className="block text-xs text-muted-foreground">{dv.settings.termStart}</label>
                  <input type="date" dir="ltr" value={eStart} onChange={(e) => setEStart(e.target.value)}
                    className="input" />
                </div>
                <div className="space-y-1">
                  <label className="block text-xs text-muted-foreground">{dv.settings.termEnd}</label>
                  <input type="date" dir="ltr" value={eEnd} onChange={(e) => setEEnd(e.target.value)}
                    className="input" />
                </div>
                <button type="submit" disabled={update.isPending}
                  className="btn-primary">
                  {dv.common.save}
                </button>
                <button type="button" onClick={() => setEditing(null)}
                  className="btn-secondary">
                  {dv.common.cancel}
                </button>
                {update.isError && (
                  <span className="text-sm text-destructive">
                    {axios.isAxiosError(update.error) && update.error.response?.status === 409 ? dv.settings.termExists : dv.common.error}
                  </span>
                )}
              </form>
            )}
          </div>
        ))}
        {remove.isError && axios.isAxiosError(remove.error) && remove.error.response?.status === 409 && (
          <p className="p-3 text-sm text-destructive">{dv.settings.termInUse}</p>
        )}
      </section>
    </div>
  );
}
